/**
 * Turns a gasless-relayer error into something a trader can act on.
 *
 * The relayer answers with a text error rather than structured revert data. It
 * may carry the raw returndata (`0x…`), a custom-error name, or — as with
 * `"RPC Request failed."` — nothing useful at all. Passing that string straight
 * to the UI tells the user only that something broke, which is what the spot
 * path used to do while the perp path decoded it.
 *
 * Rules this follows, in order:
 *   1. decode embedded returndata when the caller supplies a decoder
 *   2. map known custom-error names to plain English
 *   3. for an opaque revert, say what IS known — the contract rejected it and
 *      no funds moved — and name the usual causes WITHOUT asserting one
 *   4. otherwise pass the relayer's own words through unchanged
 *
 * Rule 3 matters: guessing a specific cause we cannot see would be worse than
 * the generic message it replaces.
 */

import { MARKET_ORDER_MESSAGES } from "../../../utils/errors.constants";

/** A relayer error string that carries no usable reason. */
const isOpaque = (msg: string): boolean =>
  /^\s*(rpc request failed|execution reverted|internal json-rpc error)\.?\s*$/i.test(
    msg
  ) || /^\s*$/.test(msg);

export interface GaslessRelayErrorOptions {
  /** The relayer's `reverted` flag, when it sent one. */
  reverted?: boolean;
  /** Shapes the opaque-revert wording; a cancel is not an order. */
  action?: "order" | "cancel";
  /** Decodes `0x…` returndata using the caller's error ABI. */
  decodeRevertData?: (hex: string) => string | undefined;
}

export const formatGaslessRelayError = (
  raw: string | undefined,
  fallback: string,
  options: GaslessRelayErrorOptions = {}
): string => {
  const msg = (raw && raw.trim()) || fallback;

  // 1. Embedded returndata — the most precise answer available.
  const hex = msg.match(/0x[0-9a-fA-F]{8,}/)?.[0];
  if (hex && options.decodeRevertData) {
    const decoded = options.decodeRevertData(hex);
    if (decoded) return decoded;
  }

  // 2. Known custom errors, by name.
  if (/DepositLockActive/i.test(msg)) {
    return "Your deposit is still settling (about one block). Please try again in a moment.";
  }
  if (/InvalidLeverage/i.test(msg)) {
    return "Invalid leverage — choose a leverage of at least 1×.";
  }
  // Observed in the wild: the relayer forwards the chain RPC's own error, and
  // the public Monad RPC rate-limits at 15 req/sec. That surfaces under the
  // same "RPC Request failed." prefix as a genuine revert, so match the
  // rate-limit detail specifically rather than lumping it in with reverts.
  if (/requests limited to|rate ?limit|429|too many requests/i.test(msg)) {
    return "The network is busy and rate-limited this request. Nothing was submitted — please try again in a few seconds.";
  }
  // Market-order bounds. Worded once in errors.constants so a relayed revert
  // and a direct one read identically.
  if (/OrderHasNoLiquidity/i.test(msg)) return MARKET_ORDER_MESSAGES.noBookLiquidity;
  if (/InsufficientLiquidity/i.test(msg)) return MARKET_ORDER_MESSAGES.insufficientLiquidity;
  if (/SlippageTooHigh/i.test(msg)) return MARKET_ORDER_MESSAGES.slippageTooHigh;
  if (/MaxQuoteInExceeded/i.test(msg)) return MARKET_ORDER_MESSAGES.maxQuoteInExceeded;
  if (/HookRejectedWithdrawal/i.test(msg)) return MARKET_ORDER_MESSAGES.collateralBackingPerps;
  if (/Insufficient(Balance|Margin)/i.test(msg)) {
    return "Insufficient balance for this order. Reduce the size or deposit more collateral.";
  }
  // "Unauthorized signer" is ALSO what a relayer says when a build is pointed
  // at the wrong chain's relayer — the exact failure src/config/relayers exists
  // to prevent. Rewriting it into "wait a moment" would erase the one string
  // that identifies a routing regression, so keep the original text visible
  // alongside the likely cause.
  if (/Unauthorized|NotApproved|InvalidSigner/i.test(msg)) {
    return (
      `${msg} — this order was not authorised on-chain. If you just enabled ` +
      "one-click trading, wait for the approval to settle and try again; if it " +
      "persists, the app may be pointed at the wrong network's relayer."
    );
  }
  if (/\bdeadline\w*/i.test(msg) && /revert|order|expire/i.test(msg)) {
    return "This order expired before it reached the exchange. Please try again.";
  }
  if (/\bnonce\w*/i.test(msg) && /revert|invalid|used|order/i.test(msg)) {
    return "This order was already used or arrived out of order. Please try again.";
  }

  // 3. The relayer said something specific — show it, even when it also set
  //    `reverted`. Replacing a real reason ("Order book is paused for
  //    maintenance") with a generic guess is strictly worse.
  if (!isOpaque(msg)) {
    return msg;
  }

  // 4. Opaque revert: state only what is certain, then list causes as
  //    possibilities. Never assert one — we genuinely cannot see which it was.
  //    `action` keeps the wording honest for cancels, which are not orders and
  //    move no funds either way.
  const subject = options.action === "cancel" ? "cancel this order" : "place this order";
  return (
    `The exchange rejected the request to ${subject} on-chain, so no funds ` +
    "were moved. This usually means an approval or deposit is still settling, " +
    "or the balance for this market is too low. Wait a moment and try again."
  );
};
